import React from 'react';
import { Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer } from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import SetVisitTimeDialog from './SetVisitTimeDialog';

const propTypes = {
  title: PropTypes.string,
  evvs: PropTypes.array,

  onEvvInPlaceUpdate: PropTypes.func.isRequired,
};

const defaultProps = {
  title: '',
  evvs: [],
};

@autobind
class TableEvvs extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      editingEvv: null,
      isSetVisitTimeDialogVisible: false,
    };
  }

  onSetVisitTimeClick(record) {
    const evv = this.props.evvs.find(e => e.getId() === record.id);

    if (evv) {
      this.setState({
        editingEvv: evv,
        isSetVisitTimeDialogVisible: true,
      });
    }
  }

  createColumns() {
    return [{
      title: 'Visit ID',
      dataIndex: 'visitId',
      sorter: (a, b) => a.visitId - b.visitId,
      defaultSortOrder: 'descend',
    }, {
      title: 'Date',
      dataIndex: 'visitDateUsString',
      sorter: (a, b) => a.visitDate - b.visitDate,
    }, {
      title: 'Type',
      dataIndex: 'type',
      sorter: (a, b) => a.type?.localeCompare(b.type),
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color={{
            SHIFT_START: 'blue_soft',
            SHIFT_END: 'yellow_gold',
          }[record.type]}
          fontSize="12px"
        >
          {record.type === 'SHIFT_START' ? 'Clock In' : 'Clock Out'}
        </styledComp.Span>
      ),
    }, {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      sorter: (a, b) => a.phoneNumber?.localeCompare(b.phoneNumber),
    }, {
      title: 'EVV Status',
      dataIndex: 'status',
      sorter: (a, b) => a.status?.localeCompare(b.status),
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color={{
            SUCCESS: 'green_light',
            PENDING: '#0000ff',
            DISCARD: 'red',
          }[record.status] || 'textPrimaryDark'}
          fontSize="12px"
        >
          {record.status}
        </styledComp.Span>
      ),
    }, {
      title: 'Call Time',
      dataIndex: 'timeUsString',
      sorter: (a, b) => a.time - b.time,
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          backgroundColor={record.offTime ? 'red' : undefined}
          color={record.offTime ? 'textPrimaryLight' : 'textPrimaryDark'}
          fontSize="12px"
        >
          {text}
        </styledComp.Span>
      ),
    }, {
      title: 'Visit Time',
      dataIndex: 'visitVisitTimeString',
      sorter: (a, b) => a.visitVisitTime - b.visitVisitTime,
      // eslint-disable-next-line
      render: (text, record) => record.status === 'PENDING' && record.visitId ? (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          style={{
            textDecoration: 'underline',
          }}
          onClick={() => this.onSetVisitTimeClick(record)}
        >
          {text || 'Set Visit Time'}
        </styledComp.A>
      ) : (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color="textPrimaryDark"
          fontSize="12px"
        >
          {text}
        </styledComp.Span>
      ),
    }, {
      title: 'Schedule Time',
      dataIndex: 'visitScheduleTimeString',
      sorter: (a, b) => a.visitScheduleTime - b.visitScheduleTime,
    }, {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      sorter: (a, b) => a.caregiverName?.localeCompare(b.caregiverName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/caregivers/caregiver_profile?id=${record.caregiverId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Service Code',
      dataIndex: 'serviceCode',
      sorter: (a, b) => a.serviceCode?.localeCompare(b.serviceCode),
    }, {
      title: 'Coordinator',
      dataIndex: 'coordinatorName',
      sorter: (a, b) => a.coordinatorName?.localeCompare(b.coordinatorName),
    }, {
      title: 'Team',
      dataIndex: 'teamName',
      sorter: (a, b) => a.teamName?.localeCompare(b.teamName),
    }, {
      title: 'Is CDPAP',
      dataIndex: 'cdpap',
      sorter: (a, b) => a.cdpap - b.cdpap,
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color="textPrimaryDark"
          fontSize="12px"
        >
          {record.cdpap ? 'YES' : 'NO'}
        </styledComp.Span>
      ),
    }, {
      title: 'Tasks',
      dataIndex: 'tasks',
      sorter: (a, b) => a.tasks?.localeCompare(b.tasks),
      render: (text, record) => (
        <Tooltip title={text}>
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            maxWidth="150px"
            color="textPrimaryDark"
            fontSize="12px"
            ellipsis
            display="inline-block"
          >
            {text}
          </styledComp.Span>
        </Tooltip>
      ),
    }];
  }

  createDataSource(evvs) {
    const dataSource = [];
    evvs.forEach((evv, index) => {
      const visit = evv.getRemoteObject('scheduleVisit');

      dataSource.push({
        key: evv.getId(),
        id: evv.getId(),
        type: evv.getString('type'),
        time: evv.getLong('time'),
        timeUsString: evv.getString('timeUsString'),
        source: evv.getString('source'),
        location: evv.getString('location'),
        phoneNumber: evv.getString('phoneNumber'),
        caregiverId: evv.getLong('caregiverId'),
        caregiverName: evv.getString('caregiverName'),
        patientId: evv.getLong('patientId'),
        patientName: evv.getString('patientName'),
        companyId: evv.getLong('companyId'),
        companyName: evv.getString('companyName'),
        serviceId: evv.getLong('serviceId'),
        serviceCode: evv.getRemoteObject('service')?.getString('displayCode'),
        scheduleVisitId: evv.getLong('scheduleVisitId'),
        coordinatorId: evv.getLong('coordinatorId'),
        coordinatorName: evv.getString('coordinatorName'),
        teamId: evv.getLong('teamId'),
        teamName: evv.getString('teamName'),
        status: evv.getString('status'),
        cdpap: evv.getBoolean('cdpap'),
        tasks: evv.getString('tasks'),
        remark: evv.getString('remark'),
        offTime: evv.getBoolean('offTime'),

        // Visit properties.
        visitId: visit?.getId(),
        visitDate: visit?.getLong('date'),
        visitDateUsString: visit?.getString('dateUsString'),
        visitFromTime: visit?.getLong('fromTime'),
        visitFromTimeString: visit?.getString('fromTimeString'),
        visitToTime: visit?.getLong('toTime'),
        visitToTimeString: visit?.getString('toTimeString'),
        visitScheduleTime: evv?.getString('type') === 'SHIFT_START'
          ? visit?.getLong('fromTime')
          : visit?.getLong('toTime'),
        visitScheduleTimeString: evv.getString('type') === 'SHIFT_START'
          ? visit?.getString('fromTimeString')
          : visit?.getString('toTimeString'),
        visitVisitTime: evv.getString('type') === 'SHIFT_START'
          ? visit?.getLong('visitIn')
          : visit?.getLong('visitOut'),
        visitVisitTimeString: evv.getString('type') === 'SHIFT_START'
          ? visit?.getString('visitInString')
          : visit?.getString('visitOutString'),
      });
    });
    return dataSource;
  }

  renderContent() {
    const { evvs, onEvvInPlaceUpdate, title, ...layoutProps } = this.props;
    const { editingEvv, isSetVisitTimeDialogVisible } = this.state;

    return (
      <FlexColContainer
        {...layoutProps}
      >
        <DataTables
          ref={(ref) => { this.dataTables = ref; }}
          width="match_parent"
          height="wrap_content"
          color="purple_wisteria"
          fontSize="12px"
          size="small"
          title={title}
          icon="check-square"
          columns={this.createColumns()}
          dataSource={this.createDataSource(evvs)}
        />

        <SetVisitTimeDialog
          width="900px"
          height="wrap_content"
          evv={editingEvv}
          isVisible={isSetVisitTimeDialogVisible}
          onVisibleChange={visible => this.setState({
            editingEvv: !visible ? null : undefined,
            isSetVisitTimeDialogVisible: visible,
          })}
          onEvvInPlaceUpdate={onEvvInPlaceUpdate}
        />
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableEvvs.propTypes = propTypes;
TableEvvs.defaultProps = defaultProps;

export default TableEvvs;
